import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, fmtPrice } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, LogOut, Package, User as UserIcon, Truck, Heart } from "lucide-react";
import { toast } from "sonner";

const GoogleBtn = ({ onClick }) => (
  <Button variant="outline" onClick={onClick} data-testid="google-login-button"
    className="w-full rounded-full border-slate-300 py-6 text-base">
    <svg className="mr-2 h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41.1 36.4 44 30.8 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
    Continuer avec Google
  </Button>
);

export default function Account() {
  const { user, login, register, logout, loginGoogle } = useAuth();
  const { skus } = useFavorites();
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // form state
  const [lEmail, setLEmail] = useState("");
  const [lPwd, setLPwd] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPwd, setRPwd] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setLoadingOrders(true);
      axios.get(`${API}/orders`).then((r) => setOrders(r.data.orders)).catch(() => {}).finally(() => setLoadingOrders(false));
      axios.get(`${API}/favorites`).then((r) => setFavs(r.data.products)).catch(() => {});
    }
  }, [user, skus]);

  if (user === undefined) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const doLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(lEmail, lPwd); toast.success("Connexion réussie"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doRegister = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await register(rName, rEmail, rPwd); toast.success("Compte créé"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- Logged out: auth forms ----
  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-14">
        <h1 className="mb-2 text-center font-head text-3xl font-extrabold text-slate-900">Espace client</h1>
        <p className="mb-8 text-center text-sm text-slate-500">Connectez-vous ou créez un compte pour suivre vos commandes.</p>

        <GoogleBtn onClick={loginGoogle} />
        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="h-px flex-1 bg-slate-200" /> OU <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Tabs defaultValue="login">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-4 pt-4" data-testid="login-form">
              <div><Label>Email</Label><Input type="email" required value={lEmail} onChange={(e) => setLEmail(e.target.value)} data-testid="login-email" className="mt-1" /></div>
              <div><Label>Mot de passe</Label><Input type="password" required value={lPwd} onChange={(e) => setLPwd(e.target.value)} data-testid="login-password" className="mt-1" /></div>
              <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-full bg-blue-600 py-6 text-white hover:bg-blue-700">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Se connecter
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-4 pt-4" data-testid="register-form">
              <div><Label>Nom</Label><Input required value={rName} onChange={(e) => setRName(e.target.value)} data-testid="register-name" className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)} data-testid="register-email" className="mt-1" /></div>
              <div><Label>Mot de passe (6 caractères min.)</Label><Input type="password" required minLength={6} value={rPwd} onChange={(e) => setRPwd(e.target.value)} data-testid="register-password" className="mt-1" /></div>
              <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full rounded-full bg-blue-600 py-6 text-white hover:bg-blue-700">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer mon compte
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ---- Logged in: dashboard ----
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-slate-200" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-700"><UserIcon className="h-7 w-7" /></div>
          )}
          <div>
            <h1 className="font-head text-2xl font-extrabold text-slate-900" data-testid="account-name">Bonjour {user.name} 👋</h1>
            <p className="text-sm text-slate-500" data-testid="account-email">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user.is_admin && (
            <Button onClick={() => navigate("/admin")} data-testid="account-admin-link" className="rounded-full bg-slate-900 text-white hover:bg-slate-700">
              <Truck className="mr-2 h-4 w-4" /> Espace admin
            </Button>
          )}
          <Button variant="outline" onClick={logout} data-testid="logout-button" className="rounded-full">
            <LogOut className="mr-2 h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </div>

      <h2 className="mb-4 mt-12 flex items-center gap-2 font-head text-xl font-extrabold text-slate-900">
        <Package className="h-5 w-5 text-blue-600" /> Mes commandes
      </h2>

      {loadingOrders ? (
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="no-orders">
          Vous n'avez pas encore de commande.
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.session_id} className="rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Commande du {new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                  <p className="font-head text-lg font-bold text-slate-900">{fmtPrice(o.amount)}</p>
                </div>
                <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">Payée</span>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span className="truncate pr-4">{it.name} × {it.quantity}</span>
                    <span>{fmtPrice(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              {o.tracking_number ? (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid={`tracking-info-${o.session_id}`}>
                  <Truck className="h-4 w-4" />
                  <span>Expédiée{o.carrier ? ` via ${o.carrier}` : ""} — suivi : <strong>{o.tracking_number}</strong></span>
                </div>
              ) : (
                <p className="mt-3 text-xs text-slate-400">En cours de préparation.</p>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-14 flex items-center gap-2 font-head text-xl font-extrabold text-slate-900">
        <Heart className="h-5 w-5 text-red-500" /> Mes favoris
      </h2>
      {favs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="no-favorites">
          Vous n'avez pas encore de favori. Cliquez sur le ♥ d'un produit pour l'enregistrer.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="favorites-list">
          {favs.map((p) => <ProductCard key={p.sku} product={p} />)}
        </div>
      )}
    </div>
  );
}
